import { useState, useEffect, useCallback } from "react";
import {
  Container,
  Center,
  Loader,
  Paper,
  Code,
  Text,
  Badge,
  NumberFormatter,
  Group,
  Button,
  Checkbox,
  Table,
  Tooltip,
  Stack,
  Box,
} from "@mantine/core";
import { getAdminJobDetails, requeueJob, requeueJobHard } from "../api";
import JobStatusBadge from "./JobStatusBadge";
import { Link } from "wouter";
import { notifications } from "@mantine/notifications";
import React from "react";

function LogLevelBadge({ size = "md", level }) {
  const levelLower = (level || "").toLowerCase();
  // The badge label is uppercase and ellipsizes when its box is narrower than the word.
  // Keep it from shrinking so "INFO" / "WARNING" stay whole inside the level column.
  const badgeStyle = { flexShrink: 0, maxWidth: "none", overflow: "visible" };
  const labelStyle = { overflow: "visible" };
  switch (levelLower) {
    case "error":
    case "critical":
      return (
        <Badge size={size} variant="light" color="red" style={badgeStyle} styles={{ label: labelStyle }}>
          {levelLower === "critical" ? "Critical" : "Error"}
        </Badge>
      );
    case "warn":
    case "warning":
      return (
        <Badge size={size} variant="light" color="yellow" style={badgeStyle} styles={{ label: labelStyle }}>
          Warning
        </Badge>
      );
    case "info":
    case "information":
      return (
        <Badge size={size} variant="light" color="blue" style={badgeStyle} styles={{ label: labelStyle }}>
          Info
        </Badge>
      );
    default:
      return (
        <Badge size={size} variant="light" color="gray" style={badgeStyle} styles={{ label: labelStyle }}>
          {level}
        </Badge>
      );
  }
}

function JobContextHeader({ header }) {
  if (!header?.fileName && !header?.uploadId && !header?.tenantId && !header?.tenantName)
    return null;

  return (
    <Stack gap={4} mb="sm">
      {header.fileName && (
        <Text fw={600} fz="md">
          {header.fileName}
        </Text>
      )}
      <Group gap="lg">
        {header.uploadId && (
          <Text fz="sm" c="dimmed">
            Upload{" "}
            <Text span ff="monospace" fz="sm" c="var(--mantine-color-text)">
              {header.uploadId}
            </Text>
          </Text>
        )}
        {(header.tenantName || header.tenantId) && (
          <Text fz="sm" c="dimmed">
            Tenant{" "}
            {header.tenantId ? (
              <Text span fz="sm" c="var(--mantine-color-text)">
                <Link to={`/tenants/${header.tenantId}`}>
                  {header.tenantName || "tenant"}
                </Link>
              </Text>
            ) : (
              header.tenantName
            )}
            {header.tenantId && (
              <Text span ff="monospace" fz="xs" ml={6}>
                {header.tenantId}
              </Text>
            )}
          </Text>
        )}
      </Group>
    </Stack>
  );
}

function JobLogDetails({ logs }) {
  return (
    <Table pl="md" mb="xl">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Timestamp</Table.Th>
          <Table.Th>Machine</Table.Th>
          <Table.Th>Duration</Table.Th>
          <Table.Th>Status</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {logs?.map((log) => (
          <>
            <Table.Tr>
              <Table.Td>{log.idCreated}</Table.Td>
              <Table.Td>{log.machineName}</Table.Td>
              <Table.Td>
                <NumberFormatter
                  thousandSeparator
                  value={log.milliseconds}
                  suffix="ms"
                />
              </Table.Td>
              <Table.Td>
                <JobStatusBadge variant="light" status={log.status} />
              </Table.Td>
            </Table.Tr>
            {log.state && (
              <Table.Tr mt="md" mb="md">
                <Table.Td colSpan={4}>
                  <Text mb="xs">State</Text>
                  <Code block style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{JSON.stringify(log.state, null, 2)}</Code>
                </Table.Td>
              </Table.Tr>
            )}
          </>
        ))}
      </Table.Tbody>
    </Table>
  );
}

export default function JobDetails({ params }) {
  const [job, setJob] = useState(null);
  const [header, setHeader] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logEntries, setLogEntries] = useState([]);
  const [jobType, setJobType] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [autoReload, setAutoReload] = useState(true); // default enabled

  const RELOAD_INTERVAL_MS = 15000; // 15s

  const loadData = useCallback(() => {
    setIsLoading(true);
    getAdminJobDetails(params.id)
      .then((data) => {
        setJob(data.job);
        setHeader(data.header ?? null);
        setLogs(data.logs);
        setJobType(data.type);
        setLogEntries(data.logEntries.toReversed() || []);
      })
      .finally(() => setIsLoading(false));
  }, [params.id]);

  useEffect(() => {
    setJob(null);
    setHeader(null);
    setLogEntries([]);
  }, [params.id]);

  useEffect(() => {
    loadData();
  }, [params.id, loadData]);

  useEffect(() => {
    if (!autoReload) return;
    const interval = setInterval(() => {
      loadData();
    }, RELOAD_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [autoReload, loadData, RELOAD_INTERVAL_MS]);

  if (!job)
    return (
      <Center mt="xl">
        <Loader size="xl" />
      </Center>
    );

  return (
    <Container fluid px={0}>
      <Paper p="md" radius="md" withBorder>
        {/* <Code block>{JSON.stringify(job, null, 2)}</Code> */}
        <JobContextHeader header={header} />
        <Group justify="space-between" align="flex-start">
          <Group>
            <Text fw={500} fz="lg" style={{ overflowWrap: "anywhere" }}>
              {job.id} ({jobType})
            </Text>
            <Text>{job.originalFileName}</Text>
            {isLoading && <Loader size="sm" />}
          </Group>
          <Group gap="md">
            <Checkbox
              size="sm"
              label="Auto-refresh (15s)"
              checked={autoReload}
              onChange={(e) => setAutoReload(e.currentTarget.checked)}
            />
            <Button
              size="xs"
              variant="light"
              onClick={loadData}
              disabled={isLoading}
            >
              Refresh
            </Button>
          </Group>
        </Group>
        <Group>
          <JobStatusBadge status={job.status} />
          <Text c="dimmed">First started at: {job.firstStarted}</Text>
        </Group>
        <Group justify="flex-end">
          <Button
            size="sm"
            my="md"
            color="blue"
            onClick={() => {
              setIsLoading(true);
              requeueJob(job.id)
                .then(() => {
                  notifications.show({
                    title: "Job re-queued",
                    color: "green",
                  });
                  loadData();
                })
                .catch((error) => {
                  notifications.show({
                    title: "Failed to re-queue job",
                    message: "" + error,
                    color: "red",
                  });
                  setIsLoading(false);
                });
            }}
          >
            Re-Queue Job
          </Button>

          <Tooltip label="Re-queues the job, setting its retry count and original start to null. It also resets progress, cancellation requests etc. It does NOT reset the job's internal state, however.">
            <Button
              size="sm"
              my="md"
              color="red"
              onClick={() => {
                setIsLoading(true);
                requeueJobHard(job.id)
                  .then(() => {
                    notifications.show({
                      title: "Job re-queued HARD",
                      color: "green",
                    });
                    loadData();
                  })
                  .catch((error) => {
                    notifications.show({
                      title: "Failed to re-queue job",
                      message: "" + error,
                      color: "red",
                    });
                    setIsLoading(false);
                  });
              }}
            >
              Re-Queue Job HARD
            </Button>
          </Tooltip>
        </Group>

        {job.documentFileId && (
          <Paper my="md">
            Processed file:{" "}
            <Link to={`/documents/${job.documentFileId}`}>
              {job.documentFileId}
            </Link>
          </Paper>
        )}

        {job.tenderId && (
          <Paper my="md">
            Processed tender:{" "}
            <Link to={`/tenders/${job.tenderId}`}>{job.tenderId}</Link>
          </Paper>
        )}

        <Stack hiddenFrom="sm" gap="md">
          {logEntries?.map((entry) => (
            <Box key={entry.id}>
              <Group gap="xs" wrap="nowrap" align="center">
                <LogLevelBadge size="xs" level={entry.level} />
                <Text ff="monospace" fz="xs" c="dimmed">
                  {entry.timestamp}
                </Text>
              </Group>
              <Text fz={9} c="dimmed" style={{ overflowWrap: "anywhere" }}>
                {entry.name}
              </Text>
              <Text fz="xs" mt={4} style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                {entry.message}
              </Text>
              {entry.exception && (
                <Code block mt="xs" style={{ fontSize: "11px", textWrap: "auto" }}>
                  {entry.exception}
                </Code>
              )}
            </Box>
          ))}
        </Stack>

        <Box visibleFrom="sm">
        <Table style={{ tableLayout: "fixed", width: "100%" }}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 88 }}>Level</Table.Th>
              <Table.Th style={{ width: 196 }}>Timestamp</Table.Th>
              <Table.Th>Message</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {logEntries?.map((entry) => (
              <React.Fragment key={entry.id}>
                <Table.Tr>
                  <Table.Td style={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                    <LogLevelBadge size="xs" level={entry.level} />
                  </Table.Td>
                  <Table.Td style={{ verticalAlign: "top" }}>
                    <Text ff="monospace" fz="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
                      {entry.timestamp}
                    </Text>
                    <Text truncate="start" fz={9} c="dimmed">
                      {entry.name}
                    </Text>
                  </Table.Td>
                  <Table.Td style={{ wordBreak: "break-word", verticalAlign: "top" }}>
                    <Text fz="xs" style={{ whiteSpace: "pre-wrap" }}>{entry.message}</Text>
                  </Table.Td>
                </Table.Tr>
                {entry.exception && (
                  <Table.Tr>
                    <Table.Td colSpan={3}>
                      {entry.exception && (
                        <Code
                          block
                          style={{
                            fontSize: "11px",
                            textWrap: "auto",
                          }}
                        >
                          {entry.exception}
                        </Code>
                      )}
                    </Table.Td>
                  </Table.Tr>
                )}
              </React.Fragment>
            ))}
          </Table.Tbody>
        </Table>
        </Box>

        <Text mt="lg" fw={500} fz="lg">
          Executions
        </Text>
        <JobLogDetails logs={logs} />
      </Paper>

      <Text mt={100}>Full job JSON</Text>
      <Code mt="sm" block style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {JSON.stringify(job, null, 2)}
      </Code>
    </Container>
  );
}
